import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Input, Button, Spin, message, Tag, Avatar, Tooltip, Modal, Image, Tabs } from 'antd';
import { SendOutlined, PlusOutlined, RobotOutlined, UserOutlined, GlobalOutlined, ArrowLeftOutlined, DeleteOutlined, MenuFoldOutlined, MenuUnfoldOutlined, PaperClipOutlined, CloseOutlined, LoadingOutlined, RightOutlined, SaveOutlined, TeamOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MdEditor from 'react-markdown-editor-lite';
import 'react-markdown-editor-lite/lib/index.css';
import { chatApi, employeesApi, agentConfigApi } from '../services/api-services';
import { resolveUrl } from '../utils/url';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTabStore } from '../store/tabStore';
import { useChatStore, connectWs } from '../store/chatStore';
import UzyToolGondang from '../components/UzyToolGondang';
import UzyToolSaveProduct from '../components/UzyToolSaveProduct';
import UzyToolSummary from '../components/UzyToolSummary';
import UzyToolDefault from '../components/UzyToolDefault';
import MessageInfo from '../components/MessageInfo';
import MessageError from '../components/MessageError';
import MessageSessionExpired from '../components/MessageSessionExpired';
import { getTagComponent, BUFFERED_TAG_NAMES } from '../components/tagRegistry';
import ModelConfigModal from '../components/ModelConfigModal';
import './Chat.css';

const EMPTY_MESSAGES: import('../store/chatStore').ChatMessage[] = [];
const { TextArea } = Input;

interface ChatInputAreaProps {
  loading: boolean;
  currentEmployee: any;
  quickMode?: boolean;
  onSwitchEmployee?: () => void;
  sessionId: string;
  storeKey: string;
  fixedEmployeeId: string | null;
  inputRef: React.RefObject<any>;
  onSend: (content: string, files: { url: string; isImage: boolean }[]) => void;
  onStop: () => void;
  onNewSession: () => void;
  onOpenModelModal: () => void;
  onOpenSaveModal: () => void;
}

const getFileTypeLabel = (fileName: string, isImage: boolean) => {
  if (isImage) return 'Image';
  const ext = fileName.split('.').pop()?.toUpperCase();
  if (ext === 'PDF') return 'PDF';
  if (ext === 'MD') return 'Markdown';
  if (['DOC', 'DOCX'].includes(ext || '')) return 'Word';
  if (['XLS', 'XLSX'].includes(ext || '')) return 'Excel';
  if (['TXT'].includes(ext || '')) return 'Text';
  return ext || 'File';
};

const getFileIcon = (fileName: string, isImage: boolean) => {
  if (isImage) return '🖼️';
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return '📄';
  if (ext === 'md') return '📝';
  if (['doc', 'docx'].includes(ext || '')) return '📘';
  if (['xls', 'xlsx'].includes(ext || '')) return '📊';
  return '📎';
};

const ChatInputArea = React.memo(({
  loading,
  currentEmployee,
  quickMode,
  onSwitchEmployee,
  sessionId,
  storeKey,
  fixedEmployeeId,
  inputRef,
  onSend,
  onStop,
  onNewSession,
  onOpenModelModal,
  onOpenSaveModal,
}: ChatInputAreaProps) => {
  const [inputValue, setInputValue] = useState('');
  const [inputMinRows, setInputMinRows] = useState(3);
  const [showCommandMenu, setShowCommandMenu] = useState(false);
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);
  const [attachments, setAttachments] = useState<{ name: string; url: string; previewUrl?: string; isImage: boolean; type?: string; size?: number }[]>([]);
  const dragStartRef = useRef<{ y: number; rows: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const commands = quickMode ? [
    { name: '/switch', desc: '切换数字人' },
    { name: '/model', desc: '切换模型配置' },
    { name: '/clear', desc: '清除会话' },
    { name: '/new', desc: '新会话' },
    { name: '/loop', desc: '循环执行' },
    { name: '/help', desc: '帮助' },
  ] : [
    { name: '/model', desc: '切换模型配置' },
    { name: '/clear', desc: '清除会话' },
    { name: '/save', desc: '保存会话' },
    { name: '/new', desc: '新会话' },
    { name: '/loop', desc: '循环执行' },
    { name: '/help', desc: '帮助' },
  ];

  const addAttachment = (name: string, url: string, previewUrl?: string, isImage: boolean = false, type?: string, size?: number) =>
    setAttachments((prev) => [...prev, { name, url, previewUrl, isImage, type, size }]);

  const removeAttachment = (idx: number) =>
    setAttachments((prev) => prev.filter((_, i) => i !== idx));

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  const handleFileUpload = async (file: File) => {
    const isImage = file.type.startsWith('image/');
    try {
      const res: any = await chatApi.uploadFile(file);
      addAttachment(file.name, res.url, isImage ? res.url : undefined, isImage, file.type, file.size);
    } catch {
      message.error('文件上传失败');
    }
  };

  const handleResizeDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    dragStartRef.current = { y: e.clientY, rows: inputMinRows };
    const onMove = (ev: MouseEvent) => {
      if (!dragStartRef.current) return;
      const delta = Math.round((dragStartRef.current.y - ev.clientY) / 20);
      setInputMinRows(Math.min(12, Math.max(2, dragStartRef.current.rows + delta)));
    };
    const onUp = () => {
      dragStartRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const imageItem = items.find((i) => i.type.startsWith('image/'));
    if (imageItem) {
      e.preventDefault();
      const blob = imageItem.getAsFile();
      if (!blob) return;
      const ext = imageItem.type.split('/')[1] || 'png';
      const file = new File([blob], `screenshot-${Date.now()}.${ext}`, { type: imageItem.type });
      handleFileUpload(file);
      return;
    }
    const files = e.clipboardData.files;
    if (files && files.length > 0) {
      e.preventDefault();
      Array.from(files).forEach((file) => handleFileUpload(file));
    }
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    if (value === '/') {
      setShowCommandMenu(true);
      setSelectedCommandIndex(0);
    } else {
      setShowCommandMenu(false);
    }
  };

  // Listen for MD editor confirm
  useEffect(() => {
    const handler = (e: Event) => setInputValue((e as CustomEvent).detail?.value ?? '');
    window.addEventListener('chat:set-input', handler);
    return () => window.removeEventListener('chat:set-input', handler);
  }, []);

  // Listen for model config updates
  useEffect(() => {
    const handler = (e: Event) => {
      const { profile, model } = (e as CustomEvent).detail ?? {};
      if (profile !== undefined) setCurrentProfileLabel(profile);
      if (model !== undefined) setCurrentModelLabel(model);
    };
    window.addEventListener('chat:model-updated', handler);
    return () => window.removeEventListener('chat:model-updated', handler);
  }, []);

  const handleCommandSelect = (cmd: string) => {
    setShowCommandMenu(false);
    setInputValue('');
    const command = cmd.slice(1).toLowerCase();
    switch (command) {
      case 'switch':
        if (quickMode && onSwitchEmployee) { onSwitchEmployee(); } else { message.warning('当前模式不支持切换数字人'); }
        return;
      case 'model': onOpenModelModal(); return;
      case 'clear':
        onNewSession();
        if (sessionId) chatApi.clearSessionContext(sessionId).catch(console.error);
        message.success('已清空当前会话');
        return;
      case 'save':
        if (quickMode) { message.warning('快捷聊天模式不支持保存会话'); } else { onOpenSaveModal(); }
        return;
      case 'help':
        message.info(quickMode ? '可用命令：/switch | /clear | /new' : '可用命令：/clear | /save | /new');
        return;
      case 'new': onNewSession(); message.success('已开始新会话'); return;
      default:
        // 后端命令：填入输入框等待发送
        setInputValue(cmd + ' ');
        inputRef.current?.focus();
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (!showCommandMenu) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedCommandIndex((prev) => (prev + 1) % commands.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedCommandIndex((prev) => (prev - 1 + commands.length) % commands.length); }
    else if (e.key === 'Enter') { e.preventDefault(); handleCommandSelect(commands[selectedCommandIndex].name); }
    else if (e.key === 'Escape') { setShowCommandMenu(false); }
  };

  const handleSend = () => {
    if (!inputValue.trim() && attachments.length === 0) return;
    const trimmedInput = inputValue.trim();
    if (trimmedInput.startsWith('/')) {
      const command = trimmedInput.split(/\s+/)[0].slice(1).toLowerCase();
      // 前端命令由菜单选择时直接执行；此处仅兜底处理用户手动输入的前端命令
      switch (command) {
        case 'switch':
          if (quickMode && onSwitchEmployee) { setInputValue(''); onSwitchEmployee(); } else { message.warning('当前模式不支持切换数字人'); }
          return;
        case 'model': setInputValue(''); onOpenModelModal(); return;
        case 'clear':
          onNewSession();
          if (sessionId) chatApi.clearSessionContext(sessionId).catch(console.error);
          setInputValue('');
          message.success('已清空当前会话');
          return;
        case 'save':
          if (quickMode) { message.warning('快捷聊天模式不支持保存会话'); } else { onOpenSaveModal(); }
          setInputValue('');
          return;
        case 'help':
          message.info(quickMode ? '可用命令：/switch | /clear | /new' : '可用命令：/clear | /save | /new');
          setInputValue('');
          return;
        case 'new': onNewSession(); setInputValue(''); message.success('已开始新会话'); return;
        // 非前端命令，透传给后端处理
      }
    }
    const attachMd = attachments.map((a) => a.isImage ? `![${a.name}](${a.url})` : `[${a.name}](${a.url})`).join('\n');
    const content = attachMd ? (trimmedInput ? `${trimmedInput}\n${attachMd}` : attachMd) : trimmedInput;
    const files = attachments.map((a) => ({ url: a.url, isImage: a.isImage }));
    setInputValue('');
    setAttachments([]);
    onSend(content, files);
  };

  // Read current model labels from store (low-frequency update, OK)
  const [currentModelLabel, setCurrentModelLabel] = useState('');
  const [currentProfileLabel, setCurrentProfileLabel] = useState('');
  useEffect(() => {
    const empReq = fixedEmployeeId
      ? agentConfigApi.getEmployeeConfig(Number(fixedEmployeeId))
      : Promise.resolve({ profile: '', model: '' });
    Promise.all([agentConfigApi.getGlobalConfig(), empReq])
      .then(([globalRes, empRes]: any) => {
        const profs = globalRes.profiles || [];
        const appliedProfile = empRes.profile || globalRes.active_profile || '';
        const active = profs.find((p: any) => p.name === appliedProfile);
        setCurrentProfileLabel(appliedProfile);
        setCurrentModelLabel(empRes.model || active?.default_model || '');
      })
      .catch(() => {});
  }, [fixedEmployeeId]);

  const backendOrigin = (process.env.REACT_APP_API_URL || '/api/v1').replace(/\/api\/v1$/, '');

  return (
    <div className="chat-input-area">
      <div className="chat-input-inner">
      {attachments.length > 0 && (
        <div className="chat-attachments">
          {attachments.map((a, i) => {
            const previewSrc = a.previewUrl?.startsWith('http') ? a.previewUrl : (a.previewUrl?.startsWith('/') ? `${backendOrigin}${a.previewUrl}` : a.previewUrl);
            return (
              <div key={i} className="chat-attachment-item">
                <div className="chat-attachment-icon">
                  {a.isImage && previewSrc ? (
                    <Image src={previewSrc} alt={a.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} preview={{ mask: '预览' }} />
                  ) : (
                    <div className="chat-attachment-icon-placeholder">{getFileIcon(a.name, a.isImage)}</div>
                  )}
                </div>
                <div className="chat-attachment-info">
                  <div className="chat-attachment-name">{a.name}</div>
                  <div className="chat-attachment-meta">{getFileTypeLabel(a.name, a.isImage)} · {formatFileSize(a.size)}</div>
                </div>
                <button className="chat-attachment-remove" onClick={() => removeAttachment(i)}>×</button>
              </div>
            );
          })}
        </div>
      )}
      <div className="chat-resize-handle" onMouseDown={handleResizeDragStart} />
      <div className="chat-input-wrapper">
        {showCommandMenu && (
          <div className="chat-command-menu">
            {commands.map((cmd, idx) => (
              <div
                key={cmd.name}
                className={`chat-command-item${idx === selectedCommandIndex ? ' active' : ''}`}
                onClick={() => handleCommandSelect(cmd.name)}
                onMouseEnter={() => setSelectedCommandIndex(idx)}
              >
                <span className="chat-command-name">{cmd.name}</span>
                <span className="chat-command-desc">{cmd.desc}</span>
              </div>
            ))}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); e.target.value = ''; }}
        />
        <TextArea
          ref={inputRef}
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleInputKeyDown}
          placeholder={currentEmployee ? `向 ${currentEmployee.name} 提问... (输入 / 查看快捷命令)` : '输入您的问题... (输入 / 查看快捷命令)'}
          autoSize={{ minRows: inputMinRows, maxRows: inputMinRows }}
          onPressEnter={(e) => { if (!e.shiftKey && !showCommandMenu) { e.preventDefault(); handleSend(); } }}
          onPaste={handlePaste}
          className="chat-input"
        />
        <div className="chat-input-actions">
          {currentModelLabel && (
            <span
              style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--apple-text-secondary, #86868b)', opacity: 0.7, cursor: 'pointer', marginRight: 'auto' }}
              onClick={onOpenModelModal}
              title="点击切换模型"
            >
              {currentModelLabel} ▾
            </span>
          )}
          <Tooltip title="上传文件">
            <Button type="text" icon={<PaperClipOutlined />} className="chat-expand-btn" onClick={() => fileInputRef.current?.click()} />
          </Tooltip>
          <Tooltip title="Markdown 编辑器">
            <Button
              type="text"
              icon={<span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'monospace', letterSpacing: -0.5 }}>MD</span>}
              className="chat-expand-btn"
              onClick={() => {
                // bubble up via custom event so parent can open MD modal with current inputValue
                const evt = new CustomEvent('chat:open-md-editor', { detail: { value: inputValue } });
                window.dispatchEvent(evt);
              }}
            />
          </Tooltip>
          <Button
            type="primary"
            shape="circle"
            icon={loading ? <span className="chat-stop-icon" /> : <SendOutlined />}
            onClick={loading ? onStop : handleSend}
            disabled={!loading && !inputValue.trim()}
            className="chat-send-btn"
          />
        </div>
      </div>
      </div>
    </div>
  );
});
interface SessionItem {
  id: number;
  session_id: string;
  title: string;
  employee_id: number | null;
  updated_at: string;
  total_input_tokens?: number;
  total_output_tokens?: number;
  total_tokens?: number;
}

const ThinkingBlock: React.FC<{ thinking: string; streaming?: boolean }> = ({ thinking, streaming }) => {
  const [open, setOpen] = useState(!!streaming);
  const contentRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => { 
    if (streaming) setOpen(true);
    else setOpen(false);
  }, [streaming]);

  useEffect(() => {
    if (streaming && open && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [thinking, streaming, open]);

  return (
    <div className="thinking-block">
      <div className="thinking-block-header" onClick={() => setOpen(v => !v)}>
        {streaming ? <LoadingOutlined className="thinking-block-icon thinking-block-icon--spin" /> : <RightOutlined className={`thinking-block-icon${open ? ' thinking-block-icon--open' : ''}`} />}
        <span className="thinking-block-label">{streaming ? '思考中...' : '已深度思考'}</span>
      </div>
      {open && <div ref={contentRef} className="thinking-block-content">{thinking.trim()}</div>}
    </div>
  );
};

const Chat: React.FC<{ 
  employeeId?: string; 
  quickMode?: boolean;
  onSwitchEmployee?: () => void;
  onMounted?: (focusInput: () => void) => void;
  initialSessionId?: string;
  storeKey?: string;
}> = ({ employeeId: employeeIdProp, quickMode, onSwitchEmployee, onMounted, initialSessionId, storeKey: storeKeyProp }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const fixedEmployeeId = employeeIdProp ?? null;
  const storeKey = storeKeyProp ?? fixedEmployeeId ?? 'default';

  // 判断是否从会话管理页进入
  const fromSessionManagement = !!searchParams.get('session');

  // All persistent state lives in the store — survives component remount
  // Split selectors: messages change every token; loading/statusText change less often
  const messages = useChatStore((s) => s.sessions[storeKey]?.messages ?? EMPTY_MESSAGES);
  const sessionId = useChatStore((s) => s.sessions[storeKey]?.sessionId ?? '');
  const loading = useChatStore((s) => s.sessions[storeKey]?.loading ?? false);
  const statusText = useChatStore((s) => s.sessions[storeKey]?.statusText ?? '正在思考...');
  const { updateSession, appendMessage, clearSession, setSessionId: storeSetSessionId } = useChatStore();

  const userAvatarUrl = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').avatar || null; } catch { return null; }
  })();
  const currentTaskId = useChatStore((s) => s.sessions[storeKey]?.currentTaskId ?? '');
  const lastVisibleMessageIndex = messages.map((msg, idx) => (msg.type === 'user' || msg.content ? idx : -1)).filter((idx) => idx >= 0).at(-1) ?? -1;
  const shouldInlineStatus = loading && lastVisibleMessageIndex >= 0 && messages[lastVisibleMessageIndex]?.type !== 'user';

  const inputRef = useRef<any>(null);

  const [mdEditorOpen, setMdEditorOpen] = useState(false);
  const [mdEditorValue, setMdEditorValue] = useState('');
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [modelModalOpen, setModelModalOpen] = useState(false);

  const [currentEmployee, setCurrentEmployee] = useState<any>(null);
  const [teamMembersMap, setTeamMembersMap] = useState<Record<string, any>>({});
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  const [historySessions, setHistorySessions] = useState<SessionItem[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(280);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const sidebarResizingRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const sidebarWidthRef = useRef(280);
  const warmupCalledRef = useRef<string | null>(null);
  const sessionsLoadedRef = useRef<string | null>(null);

  const handleSidebarResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    sidebarResizingRef.current = { startX: e.clientX, startWidth: sidebarWidthRef.current };
    document.body.style.userSelect = 'none';
    const onMove = (ev: MouseEvent) => {
      if (!sidebarResizingRef.current) return;
      const delta = sidebarResizingRef.current.startX - ev.clientX;
      const next = Math.min(480, Math.max(160, sidebarResizingRef.current.startWidth + delta));
      sidebarWidthRef.current = next;
      setSidebarWidth(next);
    };
    const onUp = () => {
      sidebarResizingRef.current = null;
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const warmupId = fixedEmployeeId ?? '0';
    if (warmupCalledRef.current === warmupId) return;
    warmupCalledRef.current = warmupId;
    if (fixedEmployeeId) {
      employeesApi.get(Number(fixedEmployeeId))
        .then((emp: any) => {
          setCurrentEmployee(emp);
          if (emp.team_members_info?.length) {
            const map: Record<string, any> = {};
            emp.team_members_info.forEach((m: any) => { if (m.identifier) map[m.identifier] = m; });
            setTeamMembersMap(map);
          }
        })
        .catch(() => {});
    }
    setDescExpanded(false);
    chatApi.warmup(Number(warmupId)).catch(() => {});
  }, [fixedEmployeeId]);

  const loadSessions = useCallback((force = false) => {
    const key = `${fixedEmployeeId ?? 'default'}-${force}`;
    if (!force && sessionsLoadedRef.current === key) return;
    if (!force) sessionsLoadedRef.current = key;
    
    const params = fixedEmployeeId ? { employee_id: Number(fixedEmployeeId) } : undefined;
    chatApi.listSessions(params).then((res: any) => setHistorySessions(res)).catch((err) => {
      console.error('加载历史会话失败:', err);
    });
  }, [fixedEmployeeId]);

  useEffect(() => { 
    loadSessions(); 
  }, [fixedEmployeeId, loadSessions]);

  // 处理从会话管理页传来的 sessionId
  useEffect(() => {
    const incomingSessionId = searchParams.get('session') || location.state?.sessionId;
    if (incomingSessionId && incomingSessionId !== sessionId) {
      handleSelectSession(incomingSessionId);
    }
  }, [searchParams, location.state?.sessionId, sessionId]);

  // Reload sessions list when loading finishes
  const prevLoadingRef = useRef(loading);
  useEffect(() => {
    if (prevLoadingRef.current && !loading) {
      loadSessions(true);
    }
    prevLoadingRef.current = loading;
  }, [loading, loadSessions])

  useEffect(() => {
    // 只在消息数量变化时滚动（新消息），流式更新不滚动
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages.length]);

  useEffect(() => {
    if (onMounted) {
      onMounted(() => inputRef.current?.focus());
    } else {
      // 普通页面路由进入时自动聚焦输入框
      const timer = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [onMounted]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setMdEditorValue(detail?.value ?? '');
      setMdEditorOpen(true);
    };
    window.addEventListener('chat:open-md-editor', handler);
    return () => window.removeEventListener('chat:open-md-editor', handler);
  }, []);


  const { addTab } = useTabStore();
  useEffect(() => {
    if (!fixedEmployeeId) return;
    const name = currentEmployee?.name;
    if (!name) return;
    const path = `/employees/${fixedEmployeeId}/chat`;
    useTabStore.getState().addTab({ key: `chat:${name}`, path, label: name, closable: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEmployee?.name, fixedEmployeeId]);

  const sendMessage = (content: string, files: { url: string; isImage: boolean }[] = []) => {
    const ws = useChatStore.getState().sessions[storeKey]?.ws;
    ws?.send(JSON.stringify({
      message: content,
      files,
      session_id: useChatStore.getState().sessions[storeKey]?.sessionId || undefined,
      employee_ids: fixedEmployeeId ? [Number(fixedEmployeeId)] : undefined,
    }));
  };

  const openModelModal = () => setModelModalOpen(true);

  // Called by ChatInputArea when user submits
  const handleSend = useCallback((content: string, files: { url: string; isImage: boolean }[]) => {
    appendMessage(storeKey, { type: 'user', content, timestamp: new Date().toLocaleTimeString() });
    updateSession(storeKey, { loading: true });
    connectWs(storeKey, () => sendMessage(content, files));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeKey]);

  const handleStop = () => {
    const ws = useChatStore.getState().sessions[storeKey]?.ws;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'stop', session_id: sessionId, task_id: currentTaskId }));
    }
  };

  const handleNewSession = () => {
    clearSession(storeKey);
  };

  const handleSelectSession = async (sid: string) => {
    if (sid === sessionId) return;
    setSidebarOpen(false);
    handleNewSession();
    storeSetSessionId(storeKey, sid);
    try {
      const msgs: any[] = await chatApi.getSessionMessages(sid) as any;
      updateSession(storeKey, {
        messages: msgs.map((m) => ({
          type: m.role,
          content: m.content,
          timestamp: new Date(m.created_at).toLocaleTimeString(),
          tokenUsage: m.total_tokens > 0 ? {
            input_tokens: m.input_tokens,
            output_tokens: m.output_tokens,
            total_tokens: m.total_tokens,
            latency: m.latency
          } : undefined
        })),
      });
    } catch (error: any) {
      if (error?.response?.status === 404) {
        message.warning('会话不存在，已清空');
        handleNewSession();
        loadSessions(true);
      } else {
        message.error('加载历史消息失败');
      }
    }
  };

  // 处理 initialSessionId prop（QuickChat 模式打开已保存会话）
  const initialSessionLoadedRef = useRef(false);
  useEffect(() => {
    if (initialSessionId && !initialSessionLoadedRef.current) {
      initialSessionLoadedRef.current = true;
      handleSelectSession(initialSessionId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSessionId]);

  const handleDeleteSession = async (e: React.MouseEvent, sid: string) => {
    e.stopPropagation();
    try {
      await chatApi.deleteSession(sid);
      if (sid === sessionId) handleNewSession();
      loadSessions(true);  // 强制刷新
    } catch {
      message.error('删除失败');
    }
  };

  const handleSaveSession = async () => {
    if (!sessionId) {
      message.warning('当前没有会话');
      return;
    }
    if (!saveName.trim()) {
      message.warning('请输入会话名称');
      return;
    }
    setSaving(true);
    try {
      await chatApi.saveSession(sessionId, saveName.trim());
      message.success('会话保存成功');
      setSaveModalOpen(false);
      setSaveName('');
    } catch (error: any) {
      message.error(error?.response?.data?.detail || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const parseMessageContent = (content: string) => {
    // 提取所有 Markdown 图片和链接
    const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    
    const images: { alt: string; src: string }[] = [];
    const files: { name: string; href: string }[] = [];
    
    let match;
    while ((match = imageRegex.exec(content)) !== null) {
      images.push({ alt: match[1], src: match[2] });
    }
    
    let textContent = content.replace(imageRegex, '');
    
    while ((match = linkRegex.exec(textContent)) !== null) {
      const href = match[2];
      if (href.startsWith('/uploads/') || href.startsWith('http')) {
        files.push({ name: match[1], href });
      }
    }
    
    textContent = textContent.replace(linkRegex, (full, name, href) => {
      if (href.startsWith('/uploads/') || href.startsWith('http')) return '';
      return full;
    });
    
    return { text: textContent.trim(), images, files };
  };

  const renderAttachments = (images: { alt: string; src: string }[], files: { name: string; href: string }[], hasText: boolean = false) => {
    if (images.length === 0 && files.length === 0) return null;
    
    const backendOrigin = (process.env.REACT_APP_API_URL || '/api/v1').replace(/\/api\/v1$/, '');
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: hasText ? 8 : 0 }}>
        {images.map((img, i) => {
          const fullSrc = img.src?.startsWith('http') ? img.src : (img.src?.startsWith('/') ? `${backendOrigin}${img.src}` : img.src);
          return (
            <div key={`img-${i}`} className="chat-attachment-item" style={{ cursor: 'pointer' }}>
              <div className="chat-attachment-icon">
                <Image src={fullSrc} alt={img.alt} style={{ width: '100%', height: '100%', objectFit: 'contain' }} preview={{ mask: '点击预览' }} />
              </div>
              <div className="chat-attachment-info">
                <div className="chat-attachment-name">{img.alt || '图片'}</div>
                <div className="chat-attachment-meta">Image</div>
              </div>
            </div>
          );
        })}
        {files.map((file, i) => {
          const fullHref = file.href?.startsWith('http') ? file.href : (file.href?.startsWith('/') ? `${backendOrigin}${file.href}` : file.href);
          return (
            <a key={`file-${i}`} href={fullHref} download target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <div className="chat-attachment-item" style={{ cursor: 'pointer' }}>
                <div className="chat-attachment-icon">
                  <div className="chat-attachment-icon-placeholder">
                    {getFileIcon(file.name, false)}
                  </div>
                </div>
                <div className="chat-attachment-info">
                  <div className="chat-attachment-name">{file.name}</div>
                  <div className="chat-attachment-meta">{getFileTypeLabel(file.name, false)}</div>
                </div>
              </div>
            </a>
          );
        })}
      </div>
    );
  };

  // 将 AI 消息按 <uzy_tool> 标签拆分为多个段落
  const splitMessageSegments = (content: string) => {
    // 匹配所有已知顶层标签：uzy_tool（带属性）以及 web_search、command 等简单标签
    const regex = /(<uzy_tool\s+([^>]*)>([\s\S]*?)<\/uzy_tool>|<(web_search|command)>([\s\S]*?)<\/\4>)/g;
    const segments: { type: string; content: string; attrs: Record<string, string> }[] = [];
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(content)) !== null) {
      const before = content.slice(lastIndex, match.index).trim();
      if (before) segments.push({ type: 'text', content: before, attrs: {} });
      if (match[2] !== undefined) {
        // uzy_tool
        const attrs: Record<string, string> = {};
        match[2].replace(/(\w+)="([^"]*)"/g, (_, k, v) => { attrs[k] = v; return ''; });
        segments.push({ type: attrs.id || attrs.type || 'unknown', content: match[3].trim(), attrs });
      } else {
        // web_search / command 等
        segments.push({ type: match[4], content: match[5].trim(), attrs: {} });
      }
      lastIndex = match.index + match[0].length;
    }
    const remaining = content.slice(lastIndex).trim();
    if (remaining) segments.push({ type: 'text', content: remaining, attrs: {} });
    return segments;
  };

  const renderAIContent = (content: string) => {
    const tagNames = BUFFERED_TAG_NAMES.filter(t => t !== 'uzy_tool').join('|');
    const combinedRegex = new RegExp(`(<(${tagNames})>[\\s\\S]*?<\\/\\2>)`, 'g');
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    const mdComponents = {
      a: ({ href, children }: any) => (
        <a href={href} target="_blank" rel="noopener noreferrer" className="chat-link">
          <GlobalOutlined className="chat-link-icon" />{children}
        </a>
      ),
      img: ({ src, alt }: any) => {
        const backendOrigin = (process.env.REACT_APP_API_URL || '/api/v1').replace(/\/api\/v1$/, '');
        const fullSrc = src?.startsWith('http') ? src : (src?.startsWith('/') ? `${backendOrigin}${src}` : src);
        return (
          <Image 
            src={fullSrc} 
            alt={alt} 
            style={{ 
              display: 'block', 
              maxWidth: '100%', 
              maxHeight: '300px', 
              borderRadius: 8, 
              marginTop: 6,
              objectFit: 'contain',
              cursor: 'pointer'
            }} 
            preview={{ mask: '点击查看' }}
          />
        );
      },
    };

    while ((match = combinedRegex.exec(content)) !== null) {
      const before = content.slice(lastIndex, match.index);
      if (before) parts.push(<ReactMarkdown key={`md-${lastIndex}`} remarkPlugins={[remarkGfm]} components={mdComponents}>{before}</ReactMarkdown>);

      const tag = match[1];
      const tagMatch = tag.match(/^<([a-zA-Z_][a-zA-Z0-9_-]*)[\s>]/);
      if (tagMatch) {
        const tagName = tagMatch[1];
        const inner = tag.replace(new RegExp(`^<${tagName}[^>]*>|</${tagName}>$`, 'g'), '').trim();
        const TagComponent = getTagComponent(tagName);
        parts.push(<TagComponent key={`tag-${match.index}`} content={inner} />);
      }

      lastIndex = match.index + match[0].length;
    }

    const remaining = content.slice(lastIndex);
    if (remaining) parts.push(<ReactMarkdown key={`md-${lastIndex}`} remarkPlugins={[remarkGfm]} components={mdComponents}>{remaining}</ReactMarkdown>);
    return parts.length > 0 ? parts : <ReactMarkdown components={mdComponents}>{content}</ReactMarkdown>;
  };

  return (
    <div className="chat-container">
      <div className="chat-main">
        <div className="chat-header">
          <div className="chat-header-left">
            {!quickMode && <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(fromSessionManagement ? '/session-management' : '/employees')} className="chat-header-back" />}
            {currentEmployee ? (
              <div className="chat-header-info">
                <Avatar size={36} className="chat-header-avatar" src={resolveUrl(currentEmployee.avatar)}>
                  {currentEmployee.name?.charAt(0)?.toUpperCase()}
                </Avatar>
                <div>
                  <div className="chat-header-name">{currentEmployee.name}</div>
                </div>
              </div>
            ) : (
              <div className="chat-header-info">
                <Avatar size={36} icon={<RobotOutlined />} className="chat-header-avatar" />
                <div className="chat-header-name">对话运行时</div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {(currentEmployee?.team_members_info?.length > 0 || currentEmployee?.experts_info?.length > 0) && (
              <Tooltip title="查看团队"><Button type="text" icon={<TeamOutlined />} onClick={() => setTeamModalOpen(true)} className="chat-header-btn" /></Tooltip>
            )}
            <Tooltip title="保存会话"><Button type="text" icon={<SaveOutlined />} onClick={() => setSaveModalOpen(true)} className="chat-header-btn" disabled={!sessionId || messages.length === 0} /></Tooltip>
            <Tooltip title="新会话"><Button type="text" icon={<PlusOutlined />} onClick={handleNewSession} className="chat-header-btn" /></Tooltip>
            <Button type="text" icon={sidebarOpen ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />} onClick={() => setSidebarOpen((v) => !v)} className="chat-header-btn" />
          </div>
        </div>

        <div className="chat-messages">
          {/* 欢迎语：无消息时展示数字人头像、名称和描述 */}
          {messages.length === 0 ? (
            <div className="chat-empty">
              <p className="chat-empty-title">有什么我能帮你的吗？</p>
              {currentEmployee?.description
                ? <div style={{
                    borderLeft: '3px solid #818cf8',
                    padding: '8px 16px',
                    maxWidth: '80%',
                    textAlign: 'left',
                    color: 'var(--apple-text-secondary)',
                    fontStyle: 'italic',
                    fontSize: 14,
                    lineHeight: 1.7,
                    opacity: 0.85,
                  }}>{currentEmployee.description.length > 250 && !descExpanded
                    ? <>{currentEmployee.description.slice(0, 250)}...<a onClick={() => setDescExpanded(true)} style={{ cursor: 'pointer', color: '#818cf8', fontStyle: 'normal' }}> 更多</a></>
                    : currentEmployee.description}
                  </div>
                : <p className="chat-empty-desc">输入您的问题，按 Enter 发送</p>}
            </div>
          ) : (
            messages.map((msg, idx) => ({ msg, idx })).filter(({ msg }) => msg.type === 'user' || msg.type === 'session_expired' || msg.content).map(({ msg, idx }) => {
              if (msg.type === 'user') {
                return (
                  <div key={idx} className="chat-message chat-message-user">
                    <div className="chat-bubble-wrapper">
                      {(() => {
                        const { text, images, files } = parseMessageContent(msg.content);
                        return (
                          <>
                            {text && (
                              <div className="chat-bubble chat-bubble-user">
                              <div className="chat-bubble-content chat-markdown">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
                                </div>
                              </div>
                            )}
                            {renderAttachments(images, files, !!text)}
                          </>
                        );
                      })()}
                      <div className="chat-bubble-meta-outside chat-bubble-meta-user">{msg.timestamp}</div>
                    </div>
                    <Avatar size={32} src={userAvatarUrl ? resolveUrl(userAvatarUrl) : undefined} icon={!userAvatarUrl ? <UserOutlined /> : undefined} className="chat-avatar-user" />
                  </div>
                );
              }
              // AI message: split by agent summary tags
              const segments = splitMessageSegments(msg.content);
              const hasToolSegments = segments.some(s => s.type !== 'text');
              if (!hasToolSegments) {
                // No summary tags, render normally
                return (
                  <div key={idx} className="chat-message chat-message-ai">
                    <Avatar size={32} className="chat-avatar-ai" src={resolveUrl(currentEmployee?.avatar)}>
                      {currentEmployee ? currentEmployee.name?.charAt(0)?.toUpperCase() : <RobotOutlined />}
                    </Avatar>
                    <div className="chat-bubble-wrapper">
                      {msg.type === 'info' ? <MessageInfo content={msg.content} /> :
                       msg.type === 'error' ? <MessageError content={msg.content} /> :
                       msg.type === 'session_expired' ? <MessageSessionExpired onNewSession={handleNewSession} /> : (
                        <div className="chat-bubble chat-bubble-ai">
                          <div className="chat-bubble-content chat-markdown">{renderAIContent(msg.content)}</div>
                          {msg.thinking && (
                            <ThinkingBlock thinking={msg.thinking} streaming={loading && idx === lastVisibleMessageIndex} />
                          )}
                          {shouldInlineStatus && idx === lastVisibleMessageIndex && (
                            <div className="chat-bubble-status"><Spin size="small" /><span>{statusText}</span></div>
                          )}
                        </div>
                      )}
                      {msg.content && msg.tokenUsage && (
                        <div className="chat-bubble-meta-outside">
                          Token: ↑ {(msg.tokenUsage.input_tokens / 1000).toFixed(2)}, ↓ {(msg.tokenUsage.output_tokens / 1000).toFixed(2)} • Time: {msg.tokenUsage.latency.toFixed(0)}ms
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
              // Has agent segments: render each as independent bubble
              return (
                <React.Fragment key={idx}>
                  {segments.map((seg, si) => {
                    if (seg.type === 'summary') {
                      const agentInfo = teamMembersMap[seg.attrs.agent];
                      return <UzyToolSummary key={`${idx}-agent-${si}`} content={seg.content} agentInfo={agentInfo} agentKey={seg.attrs.agent} renderAIContent={renderAIContent} />;
                    }
                    if (seg.type === 'gondang') {
                      return (
                        <div key={`${idx}-ticket-${si}`} className="chat-message chat-message-ai">
                          <Avatar size={32} className="chat-avatar-ai" src={resolveUrl(currentEmployee?.avatar)}>
                            {currentEmployee ? currentEmployee.name?.charAt(0)?.toUpperCase() : <RobotOutlined />}
                          </Avatar>
                          <div className="chat-bubble-wrapper">
                            <UzyToolGondang content={seg.content} expertsInfo={currentEmployee?.experts_info} />
                          </div>
                        </div>
                      );
                    }
                    if (seg.type === 'save_product') {
                      return (
                        <div key={`${idx}-product-${si}`} className="chat-message chat-message-ai">
                          <Avatar size={32} className="chat-avatar-ai" src={resolveUrl(currentEmployee?.avatar)}>
                            {currentEmployee ? currentEmployee.name?.charAt(0)?.toUpperCase() : <RobotOutlined />}
                          </Avatar>
                          <div className="chat-bubble-wrapper">
                            <UzyToolSaveProduct content={seg.content} />
                          </div>
                        </div>
                      );
                    }
                    if (seg.type !== 'text') {
                      const SegComponent = getTagComponent(seg.type);
                      return (
                        <div key={`${idx}-tool-${si}`} className="chat-message chat-message-ai">
                          <Avatar size={32} className="chat-avatar-ai" src={resolveUrl(currentEmployee?.avatar)}>
                            {currentEmployee ? currentEmployee.name?.charAt(0)?.toUpperCase() : <RobotOutlined />}
                          </Avatar>
                          <div className="chat-bubble-wrapper">
                            <SegComponent content={seg.content} attrs={seg.attrs} />
                          </div>
                        </div>
                      );
                    }
                    // text segment from manager
                    const isLast = si === segments.length - 1;
                    return (
                      <div key={`${idx}-text-${si}`} className="chat-message chat-message-ai">
                        <Avatar size={32} className="chat-avatar-ai" src={resolveUrl(currentEmployee?.avatar)}>
                          {currentEmployee ? currentEmployee.name?.charAt(0)?.toUpperCase() : <RobotOutlined />}
                        </Avatar>
                        <div className="chat-bubble-wrapper">
                          <div className="chat-bubble chat-bubble-ai">
                            <div className="chat-bubble-content chat-markdown">{renderAIContent(seg.content)}</div>
                            {isLast && msg.thinking && (
                              <ThinkingBlock thinking={msg.thinking} streaming={loading && idx === lastVisibleMessageIndex} />
                            )}
                            {isLast && shouldInlineStatus && idx === lastVisibleMessageIndex && (
                              <div className="chat-bubble-status"><Spin size="small" /><span>{statusText}</span></div>
                            )}
                          </div>
                          {isLast && msg.content && msg.tokenUsage && (
                            <div className="chat-bubble-meta-outside">
                              Token: ↑ {(msg.tokenUsage.input_tokens / 1000).toFixed(2)}, ↓ {(msg.tokenUsage.output_tokens / 1000).toFixed(2)} • Time: {msg.tokenUsage.latency.toFixed(0)}s
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })
          )}
          {loading && !shouldInlineStatus && (
            <div className="chat-message chat-message-ai">
              <Avatar size={32} className="chat-avatar-ai" src={resolveUrl(currentEmployee?.avatar)}>
                {currentEmployee ? currentEmployee.name?.charAt(0)?.toUpperCase() : <RobotOutlined />}
              </Avatar>
              <div className="chat-typing"><Spin size="small" /><span>{statusText}</span></div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <ChatInputArea
          loading={loading}
          currentEmployee={currentEmployee}
          quickMode={quickMode}
          onSwitchEmployee={onSwitchEmployee}
          sessionId={sessionId}
          storeKey={storeKey}
          fixedEmployeeId={fixedEmployeeId}
          inputRef={inputRef}
          onSend={handleSend}
          onStop={handleStop}
          onNewSession={handleNewSession}
          onOpenModelModal={() => setModelModalOpen(true)}
          onOpenSaveModal={() => setSaveModalOpen(true)}
        />

        <Modal
          open={mdEditorOpen}
          title={currentEmployee ? `与 ${currentEmployee.name} 聊天` : '对话运行时'}
          width={800}
          className="chat-md-modal"
          onCancel={() => { setMdEditorOpen(false); }}
          footer={[
            <Button key="cancel" onClick={() => { setMdEditorOpen(false); }}>取消</Button>,
            <Button
              key="confirm"
              type="primary"
              onClick={() => {
                window.dispatchEvent(new CustomEvent('chat:set-input', { detail: { value: mdEditorValue } }));
                setMdEditorOpen(false);
              }}
            >
              确认
            </Button>,
          ]}
        >
          <MdEditor
            value={mdEditorValue}
            style={{ height: 420 }}
            renderHTML={(text) => require('react-dom/server').renderToStaticMarkup(
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
            )}
            onChange={({ text }) => setMdEditorValue(text)}
          />
        </Modal>

        <Modal
          open={saveModalOpen}
          title="保存会话"
          onCancel={() => { setSaveModalOpen(false); setSaveName(''); }}
          onOk={handleSaveSession}
          confirmLoading={saving}
          okText="保存"
          cancelText="取消"
        >
          <Input
            placeholder="请输入会话名称"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            onPressEnter={handleSaveSession}
            maxLength={100}
          />
          <p style={{ marginTop: 12, fontSize: 12, color: 'var(--apple-text-tertiary)' }}>
            保存后将生成 AI 摘要，可在会话管理页面快速访问
          </p>
        </Modal>

        <ModelConfigModal
          open={modelModalOpen}
          employeeId={fixedEmployeeId ? Number(fixedEmployeeId) : undefined}
          allowedProviders={currentEmployee?.allowed_providers}
          onClose={() => setModelModalOpen(false)}
          onApplied={(profile, model) => {
            window.dispatchEvent(new CustomEvent('chat:model-updated', { detail: { profile, model } }));
          }}
        />

        <Modal
          open={teamModalOpen}
          title="团队成员"
          onCancel={() => setTeamModalOpen(false)}
          footer={null}
          width={480}
        >
          <Tabs items={[
            ...(currentEmployee?.team_members_info?.length > 0 ? [{ key: 'members', label: '数字成员', children: (
              <div className="chat-team-list">
                {currentEmployee?.team_members_info?.map((m: any) => (
                  <div key={m.id} className="chat-team-item">
                    <Avatar size={40} src={resolveUrl(m.avatar)} className="chat-agent-bubble-avatar">
                      {m.name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <div className="chat-team-item-info">
                      <div className="chat-team-item-name">{m.name}</div>
                      <div className="chat-team-item-desc">{m.description || m.identifier}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}] : []),
            ...(currentEmployee?.experts_info?.length > 0 ? [{ key: 'experts', label: '人工专家', children: (
              <div className="chat-team-list">
                {currentEmployee?.experts_info?.map((e: any) => (
                  <div key={e.id} className="chat-team-item">
                    <Avatar size={40} src={resolveUrl(e.avatar)} icon={<UserOutlined />} className="chat-agent-bubble-avatar" />
                    <div className="chat-team-item-info">
                      <div style={{display:'flex', alignItems:'center', gap:6, flexWrap:'wrap'}}>
                        <span className="chat-team-item-name">{e.name}</span>
                        {e.expertise_tags?.map((tag: string) => <Tag key={tag} style={{margin:0}}>{tag}</Tag>)}
                      </div>
                      {e.bio && <div className="chat-team-item-desc">{e.bio}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}] : []),
          ]} />
        </Modal>
      </div>

      {sidebarOpen && (
        <div
          className="chat-sidebar"
          ref={sidebarRef}
          style={{ width: sidebarWidth }}
        >
          <div className="chat-sidebar-resize-handle" onMouseDown={handleSidebarResizeStart} />
          <div className="chat-sidebar-header">
            <div className="chat-sidebar-title-group">
              <span className="chat-sidebar-title">历史会话</span>
              <Button
                size="small" danger type="text" icon={<DeleteOutlined />}
                title="清空所有会话"
                onClick={async () => {
                  await chatApi.deleteAllSessions(fixedEmployeeId ? Number(fixedEmployeeId) : undefined);
                  handleNewSession();
                  loadSessions(true);
                }}
              />
            </div>
            <Button
              size="small"
              type="text"
              icon={<CloseOutlined />}
              title="关闭历史会话列表"
              className="chat-sidebar-close-btn"
              onClick={() => setSidebarOpen(false)}
            />
          </div>
          <div className="chat-sidebar-list">
            {historySessions.length === 0 && <div className="chat-sidebar-empty">暂无会话</div>}
            {historySessions.map((s) => (
              <div
                key={s.session_id}
                className={`chat-sidebar-item${s.session_id === sessionId ? ' active' : ''}`}
                onClick={() => handleSelectSession(s.session_id)}
              >
                <div className="chat-sidebar-item-body">
                  <span className="chat-sidebar-item-title">{s.title || '新会话'}</span>
                  <span className="chat-sidebar-item-time">
                    {new Date(s.updated_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).replace(/\//g, '/').replace(',', ' ')}
                    {(s.total_tokens ?? 0) > 0 && (
                      <span style={{ marginLeft: 8, color: 'var(--apple-text-tertiary)', fontSize: 10 }}>
                        • {((s.total_tokens ?? 0) / 1000).toFixed(1)}K
                      </span>
                    )}
                  </span>
                </div>
                <Tooltip title="删除">
                  <Button
                    type="text" size="small" danger
                    icon={<DeleteOutlined />}
                    className="chat-sidebar-item-del"
                    onClick={(e) => handleDeleteSession(e, s.session_id)}
                  />
                </Tooltip>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;
